import { useCallback, useEffect, useState } from 'react';
import { flushSync } from 'react-dom';

import useSmoothNavigate from '~/modules/hooks/use-smooth-navigate';
import { useSongQueue } from '~/modules/song-queue/hooks';
import { woosh } from '~/modules/sound-manager';
import { randomInt } from '~/modules/utils/random-value';
import startViewTransition from '~/modules/utils/start-view-transition';
import useSongList from '~/routes/sing-a-song/song-selection/hooks/use-song-list';
import { useSongSelectionKeyboardNavigation } from '~/routes/sing-a-song/song-selection/hooks/use-song-selection-keyboard-navigation';
import useToolbarKeyboardNav from '~/routes/sing-a-song/song-selection/hooks/use-toolbar-keyboard-nav';

export default function useSongSelection(additionalSong: string | null, songsPerRow: number) {
  const cleanAdditionalSong = additionalSong?.replace('-new-group', '') ?? null;
  const {
    songList,
    groupedSongList,
    unverifiedSongsLoading,
    setFilters,
    filters,
    isLoading,
    selectedPlaylist,
    setSelectedPlaylist,
    playlists,
    catalogue,
  } = useSongList(cleanAdditionalSong);
  const [keyboardControl, setKeyboardControl] = useState(true);
  const [toolbarFocusMode, setToolbarFocusMode] = useState(false);
  // The filter panel is modal: while it is up, the toolbar rows and the song list both stand down.
  // Neither of them goes through `useKeyboardNav`'s `exclusive` claim - the toolbar's row switching
  // and the list's navigation are plain `useHotkeys`/`useKeyboard` - so being a modal has to be said
  // here, or the arrows drive the panel and the screen behind it at the same time.
  const [filtersOpen, setFiltersOpen] = useState(false);
  // The dock is the fourth zone on the screen, below the list. While it has the keyboard the list
  // and the toolbar stand down, the same way they do for the filter panel.
  const [queueDockActive, setQueueDockActive] = useState(false);
  const queue = useSongQueue();
  const navigate = useSmoothNavigate();

  const handleKeyboardControl = (value: boolean, additionalUpdates?: () => void) => {
    startViewTransition(() => {
      flushSync(() => {
        setKeyboardControl(value);
        // The card takes the whole keyboard, and the dock behind it is a peek rather than a
        // running order being rearranged - so it does not still hold the arrows underneath.
        if (!value) setQueueDockActive(false);
        additionalUpdates?.();
      });
    });
    woosh.play(false);
  };

  const enterQueueDock = () => {
    setToolbarFocusMode(false);
    setQueueDockActive(true);
  };
  // Stable: the dock hands it to an effect and to a hotkey, neither of which should be rebound on
  // every render of the list.
  const exitQueueDock = useCallback(() => setQueueDockActive(false), []);

  const { row1Register, row2Register, focusRow1Element, focusRow2Element, setActiveRow } = useToolbarKeyboardNav({
    enabled: toolbarFocusMode && !filtersOpen && !queueDockActive,
    onExitDown: () => {
      // keyboardControl was never changed when entering toolbar mode,
      // so we only need to clear the toolbar flag to re-enable song list nav.
      setToolbarFocusMode(false);
    },
    onExitBackspace: () => navigate('menu/'),
    onExitUp: queue.length > 0 ? enterQueueDock : undefined,
  });

  const onEnterToolbar = () => {
    // Do NOT call handleKeyboardControl(false) here — that would expand the song preview.
    // Disabling song list keyboard nav is done by passing keyboardControl && !toolbarFocusMode
    // to useSongSelectionKeyboardNavigation below.
    setToolbarFocusMode(true);
    setActiveRow(1);
    focusRow1Element('search');
  };

  const onEnterSongGroupsNav = (groupName: string) => {
    setToolbarFocusMode(true);
    setActiveRow(2);
    focusRow2Element(groupName);
  };

  const [selectedSongId, moveToSong, showFilters, setShowFilters, randomSong] = useSongSelectionKeyboardNavigation(
    // Disable song list nav while the toolbar is focused, the filter panel is up or the queue dock
    // has the keyboard, without affecting keyboardControl
    keyboardControl && !toolbarFocusMode && !filtersOpen && !queueDockActive,
    groupedSongList,
    () => handleKeyboardControl(false),
    songList.length,
    filters,
    songsPerRow,
    onEnterToolbar,
    onEnterSongGroupsNav,
    enterQueueDock,
    queue.length > 0,
  );
  const cleanSelectedSongId = selectedSongId.replace('-new-group', '');

  const [preselected, setPreselected] = useState(false);
  if (!preselected && songList.length && !isLoading) {
    let songId = '';
    const newSong = songList.find((song) => song.isNew);
    if (additionalSong && songList.some((song) => song.id === cleanAdditionalSong)) songId = additionalSong;
    else if (newSong) songId = newSong.id;
    else songId = songList[randomInt(0, songList.length - 1)].id;

    moveToSong(songId);
    setPreselected(true);
  }

  useEffect(() => {
    if (additionalSong !== null) {
      setPreselected(false);
    }
  }, [additionalSong]);

  useEffect(() => {
    if (preselected && !isLoading && songList.length && songList.some((song) => song.id === cleanSelectedSongId)) {
      /// push query param to url containing playlist name
      const url = new URL(global.location?.href);
      url.searchParams.set('song', selectedSongId!);
      global.history.replaceState(null, '', url.toString());
    }
  }, [preselected, selectedSongId, songList, isLoading, cleanSelectedSongId]);

  const songPreview = songList?.find((song) => song.id === cleanSelectedSongId);
  return {
    groupedSongList,
    focusedSong: selectedSongId,
    moveToSong,
    setKeyboardControl: handleKeyboardControl,
    keyboardControl,
    toolbarFocusMode,
    queueDockActive,
    exitQueueDock,
    filtersOpen,
    openFilters: () => setFiltersOpen(true),
    closeFilters: () => setFiltersOpen(false),
    row1Register,
    row2Register,
    songPreview,
    songList: songList ?? [],
    unverifiedSongsLoading,
    filters,
    setFilters,
    showFilters,
    setShowFilters,
    isLoading: isLoading || !preselected,
    randomSong,
    selectedPlaylist,
    setSelectedPlaylist,
    playlists,
    catalogue,
  };
}
